import { useRef, useState } from 'react';
import { appsFor } from '../lib/apps.js';
import { config } from '../lib/config.js';
import { focusableWithin, useFocusTrap } from '../lib/use-focus-trap.js';
import ExternalLink from './ExternalLink.jsx';

import tileOnguard from '../assets/app-tiles/onguard-entraid.svg';
import tileSupport from '../assets/app-tiles/ipc-support.svg';
import tileWebsite from '../assets/app-tiles/ipc-website.svg';
import tilePlatman from '../assets/app-tiles/platman.svg';

// The 40 px tile per app (brand/onguard/icons/app, light variants: the panel is
// white). IPC Global on AWS Marketplace and IdP Admin have no tile of their
// own yet: they carry the IPC website tile. Support is the official IPC globe on white; the website is the navy IPC GLOBAL tile.
const TILES = {
  'onguard-entraid': tileOnguard,
  support: tileSupport,
  website: tileWebsite,
  platman: tilePlatman,
};
const tileFor = (id) => TILES[id] ?? tileWebsite;

function Tile({ id }) {
  return <img src={tileFor(id)} alt="" width="40" height="40" className="h-10 w-10 shrink-0 rounded-md" />;
}

/**
 * The app switcher (portal workbench spec): a grid button that opens the
 * lib/apps.js list for this user and environment. Customers never see staff
 * tools; while an operator views as a client, isOperator is false too.
 *
 * Keyboard: Tab and Shift+Tab stay in the panel, ArrowDown/ArrowUp and
 * Home/End move between the app links, Escape closes and returns focus to
 * the grid button.
 */
// A quiet launcher (ENTRAID-347): a 20 px glyph in a 40 px hit area, centered on the header's midline, dimmer than the logo so the logo stays the anchor.
// Its nine marks are squares with a 2 px corner radius (design system Appendix G.1, 2026-10-25); the user avatar stays round.
// The viewBox is 1:1 with the 20 px glyph, so rx is real pixels. Each square is 5.5 px (pitch 7.25 px, gap 1.75 px), large enough that a 2 px radius still reads as a square.
export default function AppSwitcher({ isOperator, environment = config.environment, inline = false }) {
  const apps = appsFor({ isOperator: isOperator === true, environment });
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  useFocusTrap(panelRef, { active: open && !inline, onClose: () => setOpen(false), returnFocusRef: triggerRef });

  if (inline) return <AppList apps={apps} />;

  function onKeyDown(event) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const links = focusableWithin(panelRef.current);
    if (links.length === 0) return;
    event.preventDefault();
    const at = links.indexOf(document.activeElement);
    let next = 0;
    if (event.key === 'End') next = links.length - 1;
    else if (event.key === 'ArrowDown') next = at < 0 ? 0 : (at + 1) % links.length;
    else if (event.key === 'ArrowUp') next = at <= 0 ? links.length - 1 : at - 1;
    links[next].focus();
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label="IPC Global apps"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-waffle=""
        className={`flex h-10 w-10 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${open ? 'bg-white/10' : ''}`}
      >
        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          {[0, 7.25, 14.5].flatMap((y) => [0, 7.25, 14.5].map((x) => <rect key={`${x}-${y}`} x={x} y={y} width="5.5" height="5.5" rx="2" />))}
        </svg>
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="IPC Global apps"
          onKeyDown={onKeyDown}
          className="on-light absolute left-0 z-40 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-ipc-border bg-white p-2 text-ipc-text shadow-xl"
        >
          <AppList apps={apps} />
        </div>
      )}
    </div>
  );
}

function AppList({ apps }) {
  const current = apps.filter((app) => app.current);
  const others = apps.filter((app) => !app.current);
  return (
    <ul aria-label="IPC Global apps" className="space-y-1">
      {current.map((app) => (
        <li key={app.id}>
          <div aria-current="true" className="flex items-center gap-3 rounded-md border border-ipc-primary/20 bg-ipc-primary/5 px-3 py-2">
            <Tile id={app.id} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ipc-primary">{app.name}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Current app</p>
              <p className="text-xs text-slate-600">{app.description}</p>
            </div>
          </div>
        </li>
      ))}
      {current.length > 0 && others.length > 0 && (
        <li role="presentation" aria-hidden="true" className="px-1 pb-1 pt-2">
          <hr className="border-ipc-border" />
        </li>
      )}
      {others.map((app) => (
        <li key={app.id}>
          <ExternalLink
            href={app.href}
            className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ipc-light"
          >
            <Tile id={app.id} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-ipc-primary">{app.name}</span>
              <span className="block text-xs text-slate-600">{app.description}</span>
            </span>
          </ExternalLink>
        </li>
      ))}
    </ul>
  );
}
